import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, GripVertical, Save, X, Music } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { useConfirmModal } from '../common/ConfirmModal';
import './PerformanceTypeSettings.css';

interface PerformanceType {
  id: number;
  name: string;
  description?: string;
  display_order: number;
  is_active: boolean;
}

export const PerformanceTypeSettings: React.FC = () => {
  const { confirm, ConfirmModal } = useConfirmModal();
  const [performanceTypes, setPerformanceTypes] = useState<PerformanceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state for editing/adding
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchPerformanceTypes();
  }, []);

  const fetchPerformanceTypes = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/performance-types');
      setPerformanceTypes(response.data);
    } catch (error) {
      console.error('Error fetching performance types:', error);
      setError('Failed to load performance types');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = () => {
    setIsAddingNew(true);
    setEditingId(null);
    setFormData({ name: '', description: '', is_active: true });
  };

  const handleEdit = (type: PerformanceType) => {
    setEditingId(type.id);
    setIsAddingNew(false);
    setFormData({
      name: type.name,
      description: type.description || '',
      is_active: type.is_active
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAddingNew(false);
    setFormData({ name: '', description: '', is_active: true });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Performance type name is required');
      return;
    }

    try {
      setError(null);

      if (isAddingNew) {
        // Create new performance type
        const response = await apiClient.post('/performance-types', {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          is_active: formData.is_active,
          display_order: performanceTypes.length
        });
        setPerformanceTypes([...performanceTypes, response.data]);
        setSuccess('Performance type created successfully');
      } else if (editingId !== null) {
        // Update existing performance type
        const response = await apiClient.put(`/performance-types/${editingId}`, {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          is_active: formData.is_active
        });
        setPerformanceTypes(performanceTypes.map(pt =>
          pt.id === editingId ? response.data : pt
        ));
        setSuccess('Performance type updated successfully');
      }

      handleCancel();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error saving performance type:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to save performance type');
      }
    }
  };

  const handleDelete = (id: number, name: string) => {
    confirm({
      title: 'Delete Performance Type',
      message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          setError(null);
          await apiClient.delete(`/performance-types/${id}`);
          setPerformanceTypes(performanceTypes.filter(pt => pt.id !== id));
          setSuccess('Performance type deleted successfully');
          setTimeout(() => setSuccess(null), 3000);
        } catch (error: any) {
          console.error('Error deleting performance type:', error);
          setError('Failed to delete performance type. It may be in use.');
        }
      }
    });
  };

  const moveUp = async (index: number) => {
    if (index === 0) return;

    const newTypes = [...performanceTypes];
    [newTypes[index - 1], newTypes[index]] = [newTypes[index], newTypes[index - 1]];

    setPerformanceTypes(newTypes);
    await saveOrder(newTypes);
  };

  const moveDown = async (index: number) => {
    if (index === performanceTypes.length - 1) return;

    const newTypes = [...performanceTypes];
    [newTypes[index], newTypes[index + 1]] = [newTypes[index + 1], newTypes[index]];

    setPerformanceTypes(newTypes);
    await saveOrder(newTypes);
  };

  const saveOrder = async (types: PerformanceType[]) => {
    try {
      const orderedIds = types.map(t => t.id);
      await apiClient.post('/performance-types/reorder', { orderedIds });
    } catch (error) {
      console.error('Error saving order:', error);
      setError('Failed to save order');
    }
  };

  if (isLoading) {
    return (
      <div className="performance-type-settings">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="performance-type-settings">
      <div className="settings-section-header">
        <div>
          <h2>Performance Types</h2>
          <p className="text-muted">
            Define custom performance types to categorize and filter your music.
            These will appear as checkboxes in the planning page filter.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleAdd}
          disabled={isAddingNew}
        >
          <Plus size={16} className="me-1" />
          Add Performance Type
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
        </div>
      )}

      {/* Add New Form */}
      {isAddingNew && (
        <div className="performance-type-form card mb-3">
          <div className="card-body">
            <h5 className="card-title">Add New Performance Type</h5>
            <div className="mb-3">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Choir Anthem, Solo, Instrumental Prelude"
                autoFocus
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="active-checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="active-checkbox">
                Active (show in filter)
              </label>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-success" onClick={handleSave}>
                <Save size={16} className="me-1" />
                Save
              </button>
              <button className="btn btn-secondary" onClick={handleCancel}>
                <X size={16} className="me-1" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Performance Types List */}
      {performanceTypes.length === 0 ? (
        <div className="empty-state">
          <Music size={48} className="text-muted mb-3" />
          <h4>No Performance Types Yet</h4>
          <p className="text-muted">
            Get started by adding your first performance type. Examples: Choir Anthem, Congregational Hymn, Solo, Instrumental Prelude.
          </p>
        </div>
      ) : (
        <div className="performance-types-list">
          {performanceTypes.map((type, index) => (
            <div key={type.id} className="performance-type-item card mb-2">
              {editingId === type.id ? (
                <div className="card-body">
                  <h5 className="card-title">Edit Performance Type</h5>
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      autoFocus
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`edit-active-${type.id}`}
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor={`edit-active-${type.id}`}>
                      Active (show in filter)
                    </label>
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-success" onClick={handleSave}>
                      <Save size={16} className="me-1" />
                      Save
                    </button>
                    <button className="btn btn-secondary" onClick={handleCancel}>
                      <X size={16} className="me-1" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="card-body d-flex align-items-center">
                  <div className="drag-handle me-3">
                    <GripVertical size={20} className="text-muted" />
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="mb-0">{type.name}</h5>
                      {!type.is_active && (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </div>
                    {type.description && (
                      <p className="text-muted mb-0 mt-1">{type.description}</p>
                    )}
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <div className="btn-group-vertical btn-group-sm me-2">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => moveUp(index)}
                        disabled={index === 0}
                        title="Move up"
                      >
                        �
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => moveDown(index)}
                        disabled={index === performanceTypes.length - 1}
                        title="Move down"
                      >
                        �
                      </button>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleEdit(type)}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(type.id, type.name)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ConfirmModal />
    </div>
  );
};
